import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwise24Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { queryKeys } from '../lib/queryClient';
import { fetchUnsettledAch, UnsettledAch } from '../services/paymentService';
import { formatBusinessDate } from '../lib/businessTime';

const useStyles = makeStyles({
  page: {
    paddingTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('0', '16px'),
    gap: '12px',
  },
  summary: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    color: tokens.colorNeutralForeground2,
  },
  summaryAmount: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  emptyState: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  errorBanner: {
    margin: '0 16px',
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorPaletteRedBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder1),
    borderRadius: '4px',
    color: tokens.colorPaletteRedForeground1,
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
});

function formatMoney(cents: number): string {
  return ((cents || 0) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return formatBusinessDate(iso);
}

const Financial: React.FC = () => {
  const styles = useStyles();

  const { data, isLoading, error, refetch, isFetching } = useQuery<UnsettledAch[]>({
    queryKey: queryKeys.payments.achUnsettled,
    queryFn: fetchUnsettledAch,
  });

  const rows = data || [];
  const totalCents = rows.reduce((sum, r) => sum + (r.amount_cents || 0), 0);

  const columns: ColumnConfig<UnsettledAch>[] = [
    {
      columnKey: 'age_days',
      label: 'Age',
      render: (r) => (
        <Badge
          appearance={r.overdue ? 'filled' : 'tint'}
          color={r.overdue ? 'danger' : 'informative'}
        >
          {r.age_days == null ? '—' : `${r.age_days}d`}
        </Badge>
      ),
    },
    {
      columnKey: 'authorized_at',
      label: 'Authorized',
      render: (r) => formatDate(r.authorized_at),
    },
    {
      columnKey: 'customer_name',
      label: 'Customer',
      render: (r) => r.customer_name || '—',
    },
    {
      columnKey: 'order_number',
      label: 'Order',
      render: (r) => (r.order_number != null ? `#${r.order_number}` : '—'),
    },
    {
      columnKey: 'amount_cents',
      label: 'Amount',
      render: (r) => formatMoney(r.amount_cents),
    },
    {
      columnKey: 'account_last4',
      label: 'Bank account',
      render: (r) => (
        <span className={styles.mono}>
          {(r.account_type || 'acct')} ••{r.account_last4 || '????'}
        </span>
      ),
    },
    {
      columnKey: 'transaction_id',
      label: 'Auth.Net txn',
      render: (r) => <span className={styles.mono}>{r.transaction_id || '—'}</span>,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.toolbarRow}>
        <div className={styles.summary}>
          {rows.length > 0 ? (
            <>
              <span className={styles.summaryAmount}>{formatMoney(totalCents)}</span>
              <Text size={200}>
                across {rows.length} unsettled ACH {rows.length === 1 ? 'debit' : 'debits'}
              </Text>
            </>
          ) : (
            <Text size={200} className={styles.muted}>
              Unsettled ACH
            </Text>
          )}
        </div>
        <Button
          icon={<ArrowClockwise24Regular />}
          onClick={() => refetch()}
          disabled={isFetching}
          appearance="subtle"
        >
          Refresh
        </Button>
      </div>

      {error && (
        <div className={styles.errorBanner}>{(error as Error).message}</div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && (
          <div className={styles.emptyState}>Loading unsettled ACH…</div>
        )}

        {!isLoading && rows.length === 0 && !error && (
          <div className={styles.emptyState}>
            No unsettled ACH transactions — every ACH debit has settled or returned.
          </div>
        )}

        {!isLoading && rows.length > 0 && (
          <DataTable
            data={rows}
            columns={columns}
            keyField="transaction_id"
            hideableColumns
            columnVisibilityStorageKey="ach_unsettled"
            itemsPerPage={50}
            showCheckboxes={false}
            primaryColumnKey="customer_name"
          />
        )}
      </div>
    </div>
  );
};

export default Financial;
